import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';

import { RootState } from '../..';
import {
  getUserAvailabilitySlotOptionsDataAsAdmin,
  getUserAvialabilityDataByUserIdAsAdmin,
  updateUserAvialabilityAsAdmin,
} from './adminApi';
import {
  getUserAvailabilitySlotOptionsDataAsUser,
  getUserAvialabilityDataByUserIdAsUser,
  updateUserAvialabilityAsUser,
} from './api';

export type TUserAvailabilitySlot = {
  start: string;
  end: string;
};

export type TUserAvailabilitySlots = {
  Sunday: TUserAvailabilitySlot[];
  Monday: TUserAvailabilitySlot[];
  Tuesday: TUserAvailabilitySlot[];
  Wednesday: TUserAvailabilitySlot[];
  Thursday: TUserAvailabilitySlot[];
  Friday: TUserAvailabilitySlot[];
  Saturday: TUserAvailabilitySlot[];
};

export interface IUserAvailability {
  userId: string;
  slots: TUserAvailabilitySlots;
}

export type TUserAvailabilitySlotOption = {
  start: string;
  end: string;
};

export type TUserAvailabilitySlotOptionsData = {
  list: TUserAvailabilitySlotOption[];
};

export interface IInitialUserAvailabilityState {
  status: 'idle' | 'loading' | 'rejected';
  userAvailability?: IUserAvailability;
  userAvailabilitySlotOptionsData?: TUserAvailabilitySlotOptionsData;
  // ADMIN
  adminUserAvailability?: IUserAvailability;
  adminUserAvailabilitySlotOptionsData?: TUserAvailabilitySlotOptionsData;
}

const initialState: IInitialUserAvailabilityState = {
  status: 'idle',
  userAvailability: undefined,
  userAvailabilitySlotOptionsData: undefined,
  // ADMIN
  adminUserAvailability: undefined,
  adminUserAvailabilitySlotOptionsData: undefined,
};

export const UserAvailabilitySlice = createSlice({
  name: 'userAvailability',
  initialState,
  reducers: {
    resetUserAvailability: (state) => {
      state.userAvailability = undefined;
    },
    resetUserAvailabilitySlotOptionsData: (state) => {
      state.userAvailabilitySlotOptionsData = undefined;
    },
    // ADMIN
    resetAdminUserAvailability: (state) => {
      state.adminUserAvailability = undefined;
    },
    resetAdminUserAvailabilitySlotOptionsData: (state) => {
      state.adminUserAvailabilitySlotOptionsData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getUserAvialabilityDataByUserIdAsUser.fulfilled,
        (state, action) => {
          state.status = 'idle';

          const result = action.payload;

          state.userAvailability = result;
        }
      )
      .addCase(updateUserAvialabilityAsUser.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.userAvailability = result;
      })
      .addCase(
        getUserAvailabilitySlotOptionsDataAsUser.fulfilled,
        (state, action) => {
          state.status = 'idle';

          const result = action.payload;

          state.userAvailabilitySlotOptionsData = result;
        }
      );

    // ADMIN
    builder
      .addCase(updateUserAvialabilityAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.adminUserAvailability = action.payload;
      })
      .addCase(
        getUserAvialabilityDataByUserIdAsAdmin.fulfilled,
        (state, action) => {
          state.status = 'idle';
          state.adminUserAvailability = action.payload;
        }
      )
      .addCase(
        getUserAvailabilitySlotOptionsDataAsAdmin.fulfilled,
        (state, action) => {
          state.status = 'idle';
          state.adminUserAvailabilitySlotOptionsData = action.payload;
        }
      );

    builder
      .addMatcher(
        isPending(
          updateUserAvialabilityAsAdmin,
          getUserAvialabilityDataByUserIdAsAdmin,
          getUserAvialabilityDataByUserIdAsUser,
          updateUserAvialabilityAsUser,
          getUserAvailabilitySlotOptionsDataAsUser,
          getUserAvailabilitySlotOptionsDataAsAdmin
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          updateUserAvialabilityAsAdmin,
          getUserAvialabilityDataByUserIdAsAdmin,
          getUserAvialabilityDataByUserIdAsUser,
          updateUserAvialabilityAsUser,
          getUserAvailabilitySlotOptionsDataAsUser,
          getUserAvailabilitySlotOptionsDataAsAdmin
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const getUserAvailabilityState = (
  state: RootState
): RootState['userAvailability'] => state.userAvailability;

export const {
  resetAdminUserAvailability,
  resetUserAvailability,
  resetUserAvailabilitySlotOptionsData,
  resetAdminUserAvailabilitySlotOptionsData,
} = UserAvailabilitySlice.actions;

export * from './adminApi';
export * from './api';
export default UserAvailabilitySlice.reducer;
